/**
 * 配置管理器
 */

import type { EventTrackerInitOptions, ClientInfo } from '../@types'
import { isFunction, isBrowser } from './utils'
import Client from './client'

/**
 * 默认配置
 */
const DEFAULT_CONFIG: Partial<EventTrackerInitOptions> = {
  enable: true,
  debug: false,
  batchSize: 10,
  globalParams: {},
  app_id: 0,
  domain: '',
  cookie_expire: 365 * 86400000, // 365 天
  session: {
    storageKey: 'event_tracker_session',
    maxSessionDuration: 24 * 60 * 60 * 1000,
    maxIdleDuration: 30 * 60 * 1000,
  },
  cache: {
    enable: true,
    key: 'event_tracker_cache',
    maxSize: 100,
  },
  routerTracking: {
    enable: true,
    autoPageView: true,
    autoPageStay: true,
  },
}

/**
 * 配置管理器类
 */
export class ConfigManager {
  private config: EventTrackerInitOptions = {} as EventTrackerInitOptions
  private clientInfo: ClientInfo | null = null
  private timezone: number = 0
  private tz_offset: number = 0
  private client: Client | null = null

  /**
   * 初始化配置
   */
  init(options: EventTrackerInitOptions): void {
    this.config = {
      ...DEFAULT_CONFIG,
      ...options,
      session: {
        ...DEFAULT_CONFIG.session,
        ...options.session,
      },
      cache: {
        ...DEFAULT_CONFIG.cache,
        ...options.cache,
      },
      routerTracking: {
        ...DEFAULT_CONFIG.routerTracking,
        ...options.routerTracking,
      },
    } as EventTrackerInitOptions

    // 初始化时区信息
    this.timezone = new Date().getTimezoneOffset() / -60
    this.tz_offset = new Date().getTimezoneOffset() * 60
    this.initClientInfo()
  }

  /**
   * 初始化客户端信息
   */
  private initClientInfo(): void {
    try {
      const app_id = this.config.app_id || 0
      const domain = this.config.domain || ''
      const cookie_expire = this.config.cookie_expire || 365 * 86400000

      this.client = new Client(app_id, domain, cookie_expire)
      this.clientInfo = this.client.init()

      this.debug('客户端信息已采集', this.clientInfo)
    } catch (error) {
      this.error('客户端信息采集失败', error)
    }
  }

  /**
   * 获取配置项
   */
  get<K extends keyof EventTrackerInitOptions>(key: K): EventTrackerInitOptions[K] {
    return this.config[key]
  }

  /**
   * 设置配置项
   */
  set<K extends keyof EventTrackerInitOptions>(key: K, value: EventTrackerInitOptions[K]): void {
    this.config[key] = value
  }

  /**
   * 获取完整配置
   */
  getAll(): EventTrackerInitOptions {
    return { ...this.config }
  }

  /**
   * 获取设备信息
   */
  getClientInfo(): ClientInfo | null {
    return this.clientInfo
  }

  /**
   * 获取时区信息
   */
  getTimezone(): { timezone: number; tz_offset: number } {
    return {
      timezone: this.timezone,
      tz_offset: this.tz_offset,
    }
  }

  /**
   * 获取 Client 实例
   */
  getClient(): Client | null {
    return this.client
  }

  /**
   * 调用错误钩子
   */
  callErrorHook(error: Error, context?: unknown): void {
    const onError = this.get('onError')
    if (onError && isFunction(onError)) {
      try {
        onError(error, context)
      } catch (e) {
        console.error('[Event Tracker] 错误钩子执行失败', e)
      }
    }
  }

  /**
   * 调试日志
   */
  debug(message: string, ...args: unknown[]): void {
    const debug = this.get('debug')
    if (debug && isBrowser()) {
      console.log(`[Event Tracker] ${message}`, ...args)
    }
  }

  /**
   * 警告日志
   */
  warn(message: string, ...args: unknown[]): void {
    if (isBrowser()) {
      console.warn(`[Event Tracker] ${message}`, ...args)
    }
  }

  /**
   * 错误日志
   */
  error(message: string, ...args: unknown[]): void {
    if (isBrowser()) {
      console.error(`[Event Tracker] ${message}`, ...args)
    }
  }
}
